import { getActiveTimezone } from '@/utils/timezone';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { taskApi, Task, TaskStatus, TaskPriority } from '@/services/taskApi';
import { employeeApi } from '@/services/employeeApi';
import {
  ClipboardList,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Users,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { CreateTaskModal } from '@/components/tasks/CreateTaskModal';
import { BulkCreateTaskModal } from '@/components/tasks/BulkCreateTaskModal';
import SimpleSelect from '@/components/common/SimpleSelect';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: TaskStatus.PENDING, label: 'Pending' },
  { value: TaskStatus.IN_PROGRESS, label: 'In Progress' },
  { value: TaskStatus.COMPLETED, label: 'Completed' },
  { value: TaskStatus.OVERDUE, label: 'Overdue' },
  { value: TaskStatus.CANCELLED, label: 'Cancelled' },
];

const PRIORITY_FILTER_OPTIONS = [
  { value: '', label: 'All Priority' },
  { value: TaskPriority.LOW, label: 'Low' },
  { value: TaskPriority.MEDIUM, label: 'Medium' },
  { value: TaskPriority.HIGH, label: 'High' },
  { value: TaskPriority.URGENT, label: 'Urgent' },
];

const STATUS_COLORS: Record<TaskStatus, string> = {
  [TaskStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
  [TaskStatus.IN_PROGRESS]: 'bg-blue-100 text-blue-800',
  [TaskStatus.COMPLETED]: 'bg-green-100 text-green-800',
  [TaskStatus.OVERDUE]: 'bg-red-100 text-red-800',
  [TaskStatus.CANCELLED]: 'bg-gray-100 text-gray-800',
};

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  [TaskPriority.LOW]: 'bg-gray-100 text-gray-600',
  [TaskPriority.MEDIUM]: 'bg-blue-100 text-blue-600',
  [TaskPriority.HIGH]: 'bg-orange-100 text-orange-600',
  [TaskPriority.URGENT]: 'bg-red-100 text-red-600',
};

type ViewTab = 'all' | 'created';

export default function TaskManagement() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ViewTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | ''>('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const isCreatedTab = activeTab === 'created';

  // Fetch all tasks (paginated)
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['tasks', statusFilter, priorityFilter, assigneeFilter, searchQuery, page],
    queryFn: () =>
      taskApi.getTasks({
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        assignedToId: assigneeFilter || undefined,
        search: searchQuery || undefined,
        page,
        limit: 20,
      }),
    enabled: !isCreatedTab,
    staleTime: 2 * 60 * 1000,
  });

  // Fetch tasks created by me (view-only)
  const { data: createdTasks, isLoading: createdLoading } = useQuery({
    queryKey: ['created-tasks', statusFilter],
    queryFn: () => taskApi.getCreatedTasks(statusFilter || undefined),
    enabled: isCreatedTab,
  });

  // Fetch task stats (context-aware)
  const { data: statsData } = useQuery({
    queryKey: ['task-stats', activeTab],
    queryFn: () => {
      if (isCreatedTab) return taskApi.getTaskStats(false, true);
      return taskApi.getTaskStats();
    },
    staleTime: 2 * 60 * 1000,
  });

  // Fetch employees for filter
  const { data: employeesData } = useQuery({
    queryKey: ['employees-list'],
    queryFn: () => employeeApi.getEmployees({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  // Delete task mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => taskApi.deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['created-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-stats'] });
      toast.success('Task deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete task');
    },
  });

  const handleDeleteTask = (task: Task) => {
    if (confirm(`Are you sure you want to delete "${task.title}"?`)) {
      deleteMutation.mutate(task.id);
    }
  };

  const handleTabChange = (tab: ViewTab) => {
    setActiveTab(tab);
    setStatusFilter('');
    setPage(1);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: getActiveTimezone() });
  };

  const isOverdue = (task: Task) => {
    if (task.status === TaskStatus.COMPLETED || task.status === TaskStatus.CANCELLED) return false;
    return new Date(task.dueDate) < new Date();
  };

  const tasks = isCreatedTab ? createdTasks : tasksData?.data;
  const loading = isCreatedTab ? createdLoading : tasksLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6" />
            Task Management
          </h1>
          <p className="text-gray-500 mt-1">Create and manage tasks for your team</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Task
          </button>
          <button
            onClick={() => setShowBulkCreateModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            Bulk Create
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => handleTabChange('all')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'all'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          All Tasks
        </button>
        <button
          onClick={() => handleTabChange('created')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'created'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" />
            Created by Me
          </span>
        </button>
      </div>

      {/* Stats Cards */}
      {statsData && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-gray-500">Total</div>
            <div className="text-2xl font-bold text-gray-900">{statsData.total}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-yellow-600">Pending</div>
            <div className="text-2xl font-bold text-yellow-600">{statsData.pending}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-blue-600">In Progress</div>
            <div className="text-2xl font-bold text-blue-600">{statsData.inProgress}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-green-600">Completed</div>
            <div className="text-2xl font-bold text-green-600">{statsData.completed}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-red-600">Overdue</div>
            <div className="text-2xl font-bold text-red-600">{statsData.overdue}</div>
          </div>
        </div>
      )}

      {/* Created tab info banner */}
      {isCreatedTab && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-700">
          <Eye className="w-4 h-4 flex-shrink-0" />
          Showing tasks you created. Status updates are view-only.
        </div>
      )}

      {/* Filters (only on All Tasks tab) */}
      {!isCreatedTab && (
        <div className="bg-white rounded-lg border p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <SimpleSelect
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as TaskStatus | '')}
              options={STATUS_FILTER_OPTIONS}
              placeholder="All Status"
              aria-label="Filter by status"
              wrapperClassName="w-full sm:w-40"
              className="focus:!ring-blue-500 focus:!border-blue-500"
            />
            <SimpleSelect
              value={priorityFilter}
              onChange={(v) => setPriorityFilter(v as TaskPriority | '')}
              options={PRIORITY_FILTER_OPTIONS}
              placeholder="All Priority"
              aria-label="Filter by priority"
              wrapperClassName="w-full sm:w-40"
              className="focus:!ring-blue-500 focus:!border-blue-500"
            />
            <SimpleSelect
              value={assigneeFilter}
              onChange={setAssigneeFilter}
              options={[
                { value: '', label: 'All Assignees' },
                ...(employeesData?.data ?? []).map((emp: any) => ({
                  value: emp.id,
                  label: `${emp.name} (${emp.role})`,
                })),
              ]}
              placeholder="All Assignees"
              aria-label="Filter by assignee"
              wrapperClassName="w-full sm:w-48"
              className="focus:!ring-blue-500 focus:!border-blue-500"
            />
            <button
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('');
                setPriorityFilter('');
                setAssigneeFilter('');
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Tasks Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading tasks...</div>
        ) : !(tasks?.length) ? (
          <div className="p-8 text-center text-gray-500">
            <ClipboardList className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>{isCreatedTab ? 'No tasks created by you yet' : 'No tasks found'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {isCreatedTab ? 'Assigned To' : 'Assignee'}
                  </th>
                  {!isCreatedTab && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  {!isCreatedTab && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(tasks || []).map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{task.title}</div>
                      <div className="text-xs text-gray-500">{task.taskNumber}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">{task.assignedTo?.name || '-'}</div>
                      <div className="text-xs text-gray-500">{task.assignedTo?.role}</div>
                    </td>
                    {!isCreatedTab && (
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{task.createdBy?.name || '-'}</div>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[task.status]}`}>
                        {task.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[task.priority]}`}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`text-sm ${isOverdue(task) ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                        {formatDate(task.dueDate)}
                        {isOverdue(task) && <span className="ml-1 text-xs">(Overdue)</span>}
                      </div>
                    </td>
                    {!isCreatedTab && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setEditingTask(task)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTask(task)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination (only for All Tasks tab) */}
        {!isCreatedTab && tasksData && tasksData.pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Page {tasksData.pagination.page} of {tasksData.pagination.totalPages} ({tasksData.pagination.total} total)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= tasksData.pagination.totalPages}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateTaskModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries({ queryKey: ['created-tasks'] });
          }}
        />
      )}

      {showBulkCreateModal && (
        <BulkCreateTaskModal
          isOpen={showBulkCreateModal}
          onClose={() => {
            setShowBulkCreateModal(false);
            queryClient.invalidateQueries({ queryKey: ['created-tasks'] });
          }}
        />
      )}

      {editingTask && (
        <CreateTaskModal
          isOpen={!!editingTask}
          onClose={() => setEditingTask(null)}
          editTask={editingTask}
        />
      )}
    </div>
  );
}
